import React, { forwardRef, useId } from "react";
import { motion } from "framer-motion";

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  label?: string;
  labelPosition?: "left" | "right";
  className?: string;
  id?: string;
  name?: string;
}

/**
 * Arc-style Switch/Toggle component
 * 
 * Features:
 * - Smooth animated toggle
 * - Multiple sizes: sm, md, lg
 * - Optional label with position
 * - Controlled and uncontrolled modes
 * - Accessible
 */
const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked,
      defaultChecked = false,
      onChange,
      disabled = false,
      size = "md",
      label,
      labelPosition = "right",
      className = "",
      id,
      name,
    },
    ref
  ) => {
    const generatedId = useId();
    const switchId = id || generatedId;
    
    const [isChecked, setIsChecked] = React.useState(checked ?? defaultChecked);

    React.useEffect(() => {
      if (checked !== undefined) {
        setIsChecked(checked);
      }
    }, [checked]);

    const handleToggle = () => {
      if (disabled) return;
      
      const newValue = !isChecked;
      if (checked === undefined) {
        setIsChecked(newValue);
      }
      onChange?.(newValue);
    };

    // Size configurations
    const sizeConfig = {
      sm: {
        track: "w-8 h-5",
        thumb: "w-3.5 h-3.5",
        translate: isChecked ? "translateX(14px)" : "translateX(2px)",
        label: "text-sm",
      },
      md: {
        track: "w-11 h-6",
        thumb: "w-5 h-5",
        translate: isChecked ? "translateX(20px)" : "translateX(2px)",
        label: "text-sm",
      },
      lg: {
        track: "w-14 h-7",
        thumb: "w-6 h-6",
        translate: isChecked ? "translateX(28px)" : "translateX(2px)",
        label: "text-base",
      },
    };

    const config = sizeConfig[size];

    const SwitchButton = (
      <button
        ref={ref}
        id={switchId}
        name={name}
        type="button"
        role="switch"
        aria-checked={isChecked}
        disabled={disabled}
        onClick={handleToggle}
        className={`
          relative inline-flex items-center
          ${config.track}
          rounded-full
          transition-colors duration-arc-normal ease-arc
          focus:outline-none
          focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2
          ${isChecked 
            ? "bg-accent" 
            : "bg-content-muted"
          }
          ${disabled 
            ? "opacity-50 cursor-not-allowed" 
            : "cursor-pointer"
          }
        `}
      >
        <motion.span
          className={`
            ${config.thumb}
            bg-white
            rounded-full
            shadow-arc-sm
          `}
          initial={false}
          animate={{
            x: isChecked 
              ? size === "sm" ? 14 : size === "md" ? 20 : 28
              : 2,
          }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        />
      </button>
    );

    if (!label) {
      return <div className={className}>{SwitchButton}</div>;
    }

    return (
      <label
        htmlFor={switchId}
        className={`
          inline-flex items-center gap-3
          ${disabled ? "cursor-not-allowed" : "cursor-pointer"}
          ${className}
        `}
      >
        {labelPosition === "left" && (
          <span className={`${config.label} text-content-primary ${disabled ? "opacity-50" : ""}`}>
            {label}
          </span>
        )}
        {SwitchButton}
        {labelPosition === "right" && (
          <span className={`${config.label} text-content-primary ${disabled ? "opacity-50" : ""}`}>
            {label}
          </span>
        )}
      </label>
    );
  }
);

Switch.displayName = "Switch";

export default Switch;
